import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import * as promocodeApi from '../../api/promocode.api';
import type { Promocode } from '../../types/promocode';
import styles from './AdminPromocodes.module.css';

export function AdminPromocodes() {
  const { user } = useAuth();
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [code, setCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountDays, setDiscountDays] = useState('');
  const [maxUses, setMaxUses] = useState('100');
  const [validUntil, setValidUntil] = useState('');

  const fetchPromocodes = async () => {
    try {
      const data = await promocodeApi.getPromocodes();
      setPromocodes(data);
    } catch {
      setPromocodes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromocodes();
  }, []);

  const resetForm = () => {
    setCode('');
    setDiscountPercent('');
    setDiscountDays('');
    setMaxUses('100');
    setValidUntil('');
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSaving(true);
    try {
      await promocodeApi.createPromocode({
        code: code.trim().toUpperCase(),
        discountPercent: discountPercent ? Number(discountPercent) : undefined,
        discountDays: discountDays ? Number(discountDays) : undefined,
        maxUses: Number(maxUses) || 100,
        validUntil: validUntil ? new Date(validUntil).toISOString() : undefined,
      });
      setModalOpen(false);
      resetForm();
      await fetchPromocodes();
    } catch {
      // Error handled silently
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (promo: Promocode) => {
    try {
      await promocodeApi.updatePromocode(promo.id, { isActive: !promo.isActive });
      await fetchPromocodes();
    } catch {
      // Silently ignore
    }
  };

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  if (user?.role !== 'admin') {
    return (
      <div className={styles.page}>
        <div className={styles.accessDenied}>Доступ запрещён</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Промокоды</h1>
        <Button variant="primary" size="sm" onClick={() => setModalOpen(true)}>
          Создать промокод
        </Button>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="lg" />
        </div>
      ) : promocodes.length === 0 ? (
        <div className={styles.emptyState}>Промокоды не найдены</div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Код</th>
                <th>Тип</th>
                <th>Использования</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {promocodes.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className={styles.code}>{p.code}</span>
                  </td>
                  <td>
                    {p.discountPercent ? (
                      <Badge variant="info">-{p.discountPercent}%</Badge>
                    ) : p.discountDays ? (
                      <Badge variant="success">+{p.discountDays} дн.</Badge>
                    ) : (
                      <Badge variant="neutral">-</Badge>
                    )}
                  </td>
                  <td>
                    {p.currentUses} / {p.maxUses}
                  </td>
                  <td>
                    <Badge variant={p.isActive ? 'success' : 'error'}>
                      {p.isActive ? 'Активен' : 'Неактивен'}
                    </Badge>
                    {p.validUntil && (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                        до {formatDate(p.validUntil)}
                      </div>
                    )}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleToggleActive(p)}
                      >
                        {p.isActive ? 'Деактивировать' : 'Активировать'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Создать промокод">
        <h3 className={styles.modalTitle}>Создать промокод</h3>
        <form onSubmit={handleCreate}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Код</label>
            <input
              className={styles.formInput}
              placeholder="SUMMER2026"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Скидка (%)</label>
              <input
                className={styles.formInput}
                type="number"
                min="0"
                max="100"
                placeholder="20"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
              />
              <p className={styles.formHint}>Процент скидки</p>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Бонус дней</label>
              <input
                className={styles.formInput}
                type="number"
                min="0"
                placeholder="7"
                value={discountDays}
                onChange={(e) => setDiscountDays(e.target.value)}
              />
              <p className={styles.formHint}>Дополнительные дни</p>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Макс. использований</label>
              <input
                className={styles.formInput}
                type="number"
                min="1"
                placeholder="100"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Действует до</label>
              <input
                className={styles.formInput}
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              Отмена
            </Button>
            <Button variant="primary" size="sm" type="submit" loading={saving}>
              Создать
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
